import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardMedia,
  Link,
  useTheme,
  CircularProgress,
} from '@mui/material';

const DigitalArtwork = () => {
  const theme = useTheme();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArtworks = async () => {
      // Use rss2json to convert RSS to JSON and bypass CORS/polyfill issues
      const RSS_URL = `https://backend.deviantart.com/rss.xml?q=by%3Adhineline+sort%3Atime+meta%3Aall`;
      const API_URL = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(RSS_URL)}`;

      // Fisher-Yates (aka Knuth) Shuffle function
      const shuffleArray = (array) => {
        let currentIndex = array.length,  randomIndex;
        // While there remain elements to shuffle.
        while (currentIndex !== 0) {
          // Pick a remaining element.
          randomIndex = Math.floor(Math.random() * currentIndex);
          currentIndex--;
          // And swap it with the current element.
          [array[currentIndex], array[randomIndex]] = [
            array[randomIndex], array[currentIndex]];
        }
        return array;
      }

      try {
        const response = await fetch(API_URL);
        const data = await response.json();
        if (data.status === 'ok') {
          // Shuffle the items and then take the first 10
          const shuffledItems = shuffleArray(data.items);
          setArtworks(shuffledItems.slice(0, 10));
        } else {
          throw new Error(data.message);
        }
      } catch (error) {
        console.error('Failed to fetch artworks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArtworks();
  }, []);

  return (
    <Box
      id="artwork"
      sx={{
        width: '100vw',
        position: 'relative',
        left: '50%',
        right: '50%',
        marginLeft: '-50vw',
        marginRight: '-50vw',
        py: 8,
        background: theme.palette.background.default,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Digital Artwork
          </Typography>
          <Typography variant="h5" color="text.secondary">
            I love using Midjourney and other tools to make digital artwork when I have the time.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 0,
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(5, 1fr)',
              },
            }}
          >
            {artworks.map((item, index) => (
              <Link href={item.link} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none', lineHeight: 0 }} key={index}>
                <Card
                  sx={{
                    borderRadius: 0,
                    boxShadow: 'none',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      zIndex: 10,
                      position: 'relative',
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={item.thumbnail}
                    alt={item.title}
                    sx={{
                      aspectRatio: '1 / 1',
                      width: '100%',
                      height: 'auto',
                    }}
                  />
                </Card>
              </Link>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default DigitalArtwork; 